import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiCalendar, FiClock, FiMapPin, FiFileText, FiChevronDown, FiChevronUp } from "react-icons/fi";
import "./Schedules.css";

const Schedule = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);
  const [paperDetails, setPaperDetails] = useState({});
  const [loadingPapers, setLoadingPapers] = useState({});

  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/schedules`
        );
        
        // Sort sessions by date then by time
        const sortedSessions = response.data.sort((a, b) => {
          const dateCompare = new Date(a.date) - new Date(b.date);
          if (dateCompare !== 0) return dateCompare;
          return a.timeFrom.localeCompare(b.timeFrom);
        });
        
        setSessions(sortedSessions);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching schedules:", error);
        setError("Failed to load schedules. Please try again later.");
        setLoading(false);
      }
    };

    fetchSchedules();
  }, []);

  const fetchPaperDetails = async (sessionId, paperIds) => {
    try {
      setLoadingPapers(prev => ({ ...prev, [sessionId]: true }));
      
      // Fetch details for all papers in parallel
      const paperRequests = paperIds.map(paperId => 
        axios.get(`${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/get-paper/${paperId}`)
          .then(res => res.data) // Get the entire response
          .catch(error => {
            console.error(`Error fetching paper ${paperId}:`, error);
            return {
              data: {
                paperID: paperId,
                title: `Paper #${paperId} (Details Unavailable)`
              },
              error: true
            };
          })
      );
      
      const responses = await Promise.all(paperRequests);
      const newPaperDetails = { ...paperDetails };
      
      responses.forEach(response => {
        if (response && response.data) {
          const paper = response.data;
          newPaperDetails[paper.paperID] = paper;
        }
      });
      
      setPaperDetails(newPaperDetails);
    } catch (error) {
      console.error("Error fetching paper details:", error);
    } finally {
      setLoadingPapers(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const formatTime = (time) => {
    // Directly format HH:MM string to AM/PM format
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    
    return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const toggleSession = (sessionId) => {
    const session = sessions.find(s => s._id === sessionId);
    
    // If session is being expanded and we don't have paper details yet
    if (expandedSession !== sessionId && session && session.papers.length > 0) {
      const missingPapers = session.papers.filter(id => !paperDetails[id]);
      
      if (missingPapers.length > 0) {
        fetchPaperDetails(sessionId, missingPapers);
      }
    }
    
    setExpandedSession(expandedSession === sessionId ? null : sessionId);
  };

  const getSessionPapers = (session) => {
    return session.papers.map(paperId => {
      if (paperDetails[paperId]) {
        return paperDetails[paperId];
      }
      
      return {
        paperID: paperId,
        title: `Paper #${paperId}`,
        loading: true
      };
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading schedules...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-icon">!</div>
        <p className="error-message">{error}</p>
        <button 
          className="retry-button"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="schedule-container">
      <div className="schedule-header">
        <div className="processTitle">
          <h1>Program Schedule</h1>
        </div>
        <p className="schedule-subtitle">Conference Timeline & Sessions</p>
      </div>

      {sessions.length === 0 ? (
        <div className="empty-state">
          <FiFileText size={48} />
          <h3>No Sessions Scheduled Yet</h3>
          <p>Add sessions to create the conference program schedule.</p>
        </div>
      ) : (
        <div className="sessions-list">
          {sessions.map((session) => {
            const sessionPapers = getSessionPapers(session);
            const isExpanded = expandedSession === session._id;
            const isLoading = loadingPapers[session._id];
            
            return (
              <div 
                key={session._id} 
                className={`session-card ${isExpanded ? 'expanded' : ''}`}
              >
                <div 
                  className="session-header" 
                  onClick={() => toggleSession(session._id)}
                >
                  <div className="session-info">
                    <div className="session-date">
                      <FiCalendar className="session-icon" />
                      <span>{formatDate(session.date)}</span>
                    </div>
                    <h3 className="session-name">{session.session}</h3>
                    <div className="session-meta">
                      <span className="session-type">
                        <FiFileText className="session-icon" />
                        {session.presentationType} Session
                      </span>
                      <span className="session-time">
                        <FiClock className="session-icon" />
                        {formatTime(session.timeFrom)} - {formatTime(session.timeTo)}
                      </span>
                      <span className="session-location">
                        <FiMapPin className="session-icon" />
                        {session.room ? `${session.room} • ` : ''}{session.venue}
                      </span>
                    </div>
                  </div>
                  <div className="session-toggle">
                    <span className="paper-count">
                      {session.papers.length} paper{session.papers.length !== 1 ? 's' : ''}
                    </span>
                    <span className="toggle-icon">
                      {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                    </span>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="session-papers">
                    <div className="papers-header">
                      <h4>Papers in this Session</h4>
                      {isLoading && (
                        <div className="loading-papers">
                          <div className="mini-spinner"></div>
                          <span>Loading paper details...</span>
                        </div>
                      )}
                    </div>
                    <div className="papers-list">
                      {sessionPapers.map((paper) => (
                        <div key={paper.paperID} className="paper-item">
                          <span className="paper-id">Paper ID: {paper.paperID}</span>
                          <div className="paper-title-container">
                            <span className={`paper-title ${paper.loading ? 'loading' : ''}`}>
                              {paper.title}
                              {paper.error && <span className="error-tag">Details unavailable</span>}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Schedule;